import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, X, Link as LinkIcon, Key, Globe } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

// ==================== ROLE FORM ====================
export function RoleFormDialog({ open, onOpenChange, role, roles, departments, systems, onSave }) {
  const [formData, setFormData] = useState({
    title: "",
    department: "",
    reports_to: "",
    purpose: "",
    responsibilities: [],
    kras: [],
    kpis: [],
    inputs_received: [],
    outputs_produced: [],
    dependencies: [],
    software_used: [],
    handoffs: [],
    training_assets: [],
    photo_url: "",
  });
  const [newItem, setNewItem] = useState("");
  const [newKpi, setNewKpi] = useState({ name: "", target: "" });
  const [newHandoff, setNewHandoff] = useState({ to: "", what: "" });
  const [newAsset, setNewAsset] = useState({ name: "", url: "", type: "url" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setFormData({
        title: role.title || "",
        department: role.department || "",
        reports_to: role.reports_to || "",
        purpose: role.purpose || "",
        responsibilities: role.responsibilities || [],
        kras: role.kras || [],
        kpis: role.kpis || [],
        inputs_received: role.inputs_received || [],
        outputs_produced: role.outputs_produced || [],
        dependencies: role.dependencies || [],
        software_used: role.software_used || [],
        handoffs: role.handoffs || [],
        training_assets: role.training_assets || [],
        photo_url: role.photo_url || "",
      });
    } else {
      setFormData({
        title: "",
        department: "",
        reports_to: "",
        purpose: "",
        responsibilities: [],
        kras: [],
        kpis: [],
        inputs_received: [],
        outputs_produced: [],
        dependencies: [],
        software_used: [],
        handoffs: [],
        training_assets: [],
        photo_url: "",
      });
    }
  }, [role, open]);

  const handleSave = async () => {
    if (!formData.title || !formData.department) {
      toast.error("Title and Department are required");
      return;
    }
    setSaving(true);
    try {
      if (role?.role_id) {
        await axios.patch(`${API}/roles/${role.role_id}`, formData, { withCredentials: true });
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/roles`, formData, { withCredentials: true });
        toast.success("Role created");
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const addToArray = (field, value) => {
    if (value && !formData[field].includes(value)) {
      setFormData({ ...formData, [field]: [...formData[field], value] });
    }
    setNewItem("");
  };

  const removeFromArray = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const addKpi = () => {
    if (newKpi.name && newKpi.target) {
      setFormData({ ...formData, kpis: [...formData.kpis, { ...newKpi }] });
      setNewKpi({ name: "", target: "" });
    }
  };

  const addHandoff = () => {
    if (newHandoff.to && newHandoff.what) {
      setFormData({ ...formData, handoffs: [...formData.handoffs, { ...newHandoff }] });
      setNewHandoff({ to: "", what: "" });
    }
  };

  const addAsset = () => {
    if (newAsset.name && newAsset.url) {
      setFormData({ ...formData, training_assets: [...formData.training_assets, { ...newAsset }] });
      setNewAsset({ name: "", url: "", type: "url" });
    }
  };

  const uniqueDepartments = [...new Set(departments.map(d => d.name))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{role ? "Edit Role" : "Create New Role"}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="kpi">KPIs & KRAs</TabsTrigger>
              <TabsTrigger value="flows">Flows & Handoffs</TabsTrigger>
              <TabsTrigger value="assets">Training Assets</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div>
                <Label>Role Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-secondary/50 shrink-0">
                    {formData.photo_url ? (
                      <img
                        src={formData.photo_url}
                        alt="Role"
                        className="w-full h-full object-cover"
                        data-testid="role-photo-preview"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground text-center px-2">No photo</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      data-testid="role-photo-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error("Image must be under 2MB");
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (evt) => {
                          setFormData((prev) => ({ ...prev, photo_url: evt.target.result || "" }));
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="Or paste an image URL"
                        value={formData.photo_url?.startsWith("data:") ? "" : formData.photo_url}
                        onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                        data-testid="role-photo-url"
                      />
                      {formData.photo_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({ ...formData, photo_url: "" })}
                          data-testid="role-photo-remove"
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Upload an image (max 2MB) or paste a URL</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Project Lead"
                  />
                </div>
                <div>
                  <Label>Department *</Label>
                  <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {uniqueDepartments.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Reports To</Label>
                <Select value={formData.reports_to || "none"} onValueChange={(v) => setFormData({ ...formData, reports_to: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level)</SelectItem>
                    {roles.filter(r => r.role_id !== role?.role_id).map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Purpose</Label>
                <Textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Describe the purpose of this role..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Responsibilities</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Add responsibility"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("responsibilities", newItem))}
                  />
                  <Button type="button" size="sm" onClick={() => addToArray("responsibilities", newItem)}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.responsibilities.map((r, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {r}
                      <X size={12} className="cursor-pointer" onClick={() => removeFromArray("responsibilities", i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Software Used</Label>
                <Select onValueChange={(v) => addToArray("software_used", v)}>
                  <SelectTrigger><SelectValue placeholder="Select software" /></SelectTrigger>
                  <SelectContent>
                    {systems.map((s) => (
                      <SelectItem key={s.system_id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.software_used.map((s, i) => (
                    <Badge key={i} variant="outline" className="gap-1 bg-red-50 text-red-700">
                      {s}
                      <X size={12} className="cursor-pointer" onClick={() => removeFromArray("software_used", i)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="kpi" className="space-y-4">
              <div>
                <Label>Key Result Areas (KRAs)</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Add KRA"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("kras", newItem))}
                  />
                  <Button type="button" size="sm" onClick={() => addToArray("kras", newItem)}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="space-y-1">
                  {formData.kras.map((k, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-primary/5 rounded">
                      <span className="text-sm">{k}</span>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("kras", i)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Key Performance Indicators (KPIs)</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newKpi.name}
                    onChange={(e) => setNewKpi({ ...newKpi, name: e.target.value })}
                    placeholder="KPI Name"
                    className="flex-1"
                  />
                  <Input
                    value={newKpi.target}
                    onChange={(e) => setNewKpi({ ...newKpi, target: e.target.value })}
                    placeholder="Target (e.g., 95%)"
                    className="w-32"
                  />
                  <Button type="button" size="sm" onClick={addKpi}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {formData.kpis.map((kpi, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-accent/10 border border-accent/20 rounded">
                      <div>
                        <p className="text-xs text-muted-foreground">{kpi.name}</p>
                        <p className="font-semibold">{kpi.target}</p>
                      </div>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("kpis", i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="flows" className="space-y-4">
              <div>
                <Label>Inputs Received</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Add input"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("inputs_received", newItem))}
                  />
                  <Button type="button" size="sm" onClick={() => addToArray("inputs_received", newItem)}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.inputs_received.map((inp, i) => (
                    <Badge key={i} variant="secondary" className="gap-1 bg-green-50 text-green-700">
                      {inp}
                      <X size={12} className="cursor-pointer" onClick={() => removeFromArray("inputs_received", i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Outputs Produced</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Add output"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("outputs_produced", newItem))}
                  />
                  <Button type="button" size="sm" onClick={() => addToArray("outputs_produced", newItem)}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.outputs_produced.map((out, i) => (
                    <Badge key={i} variant="secondary" className="gap-1 bg-blue-50 text-blue-700">
                      {out}
                      <X size={12} className="cursor-pointer" onClick={() => removeFromArray("outputs_produced", i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Dependencies</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Add dependency"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("dependencies", newItem))}
                  />
                  <Button type="button" size="sm" onClick={() => addToArray("dependencies", newItem)}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.dependencies.map((dep, i) => (
                    <Badge key={i} variant="outline" className="gap-1">
                      {dep}
                      <X size={12} className="cursor-pointer" onClick={() => removeFromArray("dependencies", i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Handoffs</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newHandoff.to}
                    onChange={(e) => setNewHandoff({ ...newHandoff, to: e.target.value })}
                    placeholder="To (team/role)"
                    className="flex-1"
                  />
                  <Input
                    value={newHandoff.what}
                    onChange={(e) => setNewHandoff({ ...newHandoff, what: e.target.value })}
                    placeholder="What is handed off"
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={addHandoff}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="space-y-1">
                  {formData.handoffs.map((h, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-secondary rounded">
                      <span className="text-sm"><strong>{h.to}</strong> → {h.what}</span>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("handoffs", i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="assets" className="space-y-4">
              <div>
                <Label>Training Assets & SOPs</Label>
                <p className="text-xs text-muted-foreground mb-2">Add links to documents, SOPs, or API connections</p>
                <div className="space-y-2 mb-2 p-3 border rounded-md bg-secondary/30">
                  <div className="flex gap-2">
                    <Input
                      value={newAsset.name}
                      onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                      placeholder="Asset name"
                      className="flex-1"
                    />
                    <Select value={newAsset.type} onValueChange={(v) => setNewAsset({ ...newAsset, type: v })}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="url"><Globe size={14} className="inline mr-1" />URL</SelectItem>
                        <SelectItem value="gdrive"><LinkIcon size={14} className="inline mr-1" />Google Drive</SelectItem>
                        <SelectItem value="api"><Key size={14} className="inline mr-1" />API</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newAsset.url}
                      onChange={(e) => setNewAsset({ ...newAsset, url: e.target.value })}
                      placeholder={newAsset.type === "api" ? "API endpoint or connection string" : "URL or link"}
                      className="flex-1"
                    />
                    <Button type="button" size="sm" onClick={addAsset}>
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {formData.training_assets.map((asset, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-secondary rounded">
                      <div className="flex items-center gap-2">
                        {asset.type === "api" ? <Key size={16} className="text-violet-600" /> : 
                         asset.type === "gdrive" ? <LinkIcon size={16} className="text-green-600" /> : 
                         <Globe size={16} className="text-blue-600" />}
                        <div>
                          <p className="text-sm font-medium">{asset.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-md">{asset.url}</p>
                        </div>
                      </div>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("training_assets", i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : (role ? "Update Role" : "Create Role")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DEPARTMENT FORM ====================
export function DepartmentFormDialog({ open, onOpenChange, department, roles, systems, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    head_role_id: "",
    system_touchpoints: [],
    bottlenecks: [],
    escalation_paths: [],
    color: "#002FA7",
  });
  const [newItem, setNewItem] = useState("");
  const [newEscalation, setNewEscalation] = useState({ from: "", to: "" });
  const [saving, setSaving] = useState(false);

  const colors = [
    "#002FA7", "#059669", "#7C3AED", "#DC2626", "#CA8A04", "#0891B2", "#DB2777", "#4F46E5", "#EA580C"
  ];

  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name || "",
        description: department.description || "",
        head_role_id: department.head_role_id || "",
        system_touchpoints: department.system_touchpoints || [],
        bottlenecks: department.bottlenecks || [],
        escalation_paths: department.escalation_paths || [],
        color: department.color || "#002FA7",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        head_role_id: "",
        system_touchpoints: [],
        bottlenecks: [],
        escalation_paths: [],
        color: "#002FA7",
      });
    }
  }, [department, open]);

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (department?.department_id) {
        await axios.patch(`${API}/departments/${department.department_id}`, formData, { withCredentials: true });
        toast.success("Department updated");
      } else {
        await axios.post(`${API}/departments`, formData, { withCredentials: true });
        toast.success("Department created");
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save department");
    } finally {
      setSaving(false);
    }
  };

  const addToArray = (field, value) => {
    if (value && !formData[field].includes(value)) {
      setFormData({ ...formData, [field]: [...formData[field], value] });
    }
    setNewItem("");
  };

  const removeFromArray = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const addEscalation = () => {
    if (newEscalation.from && newEscalation.to) {
      setFormData({ ...formData, escalation_paths: [...formData.escalation_paths, { ...newEscalation }] });
      setNewEscalation({ from: "", to: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{department ? "Edit Department" : "Create New Department"}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sales"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2 mt-1">
                  {colors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 ${formData.color === c ? "border-foreground" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setFormData({ ...formData, color: c })}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the department..."
                rows={3}
              />
            </div>

            <div>
              <Label>Department Head</Label>
              <Select value={formData.head_role_id || "none"} onValueChange={(v) => setFormData({ ...formData, head_role_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select head role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>System Touchpoints</Label>
              <Select onValueChange={(v) => addToArray("system_touchpoints", v)}>
                <SelectTrigger><SelectValue placeholder="Select system" /></SelectTrigger>
                <SelectContent>
                  {systems.map((s) => (
                    <SelectItem key={s.system_id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.system_touchpoints.map((s, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 bg-red-50 text-red-700">
                    {s}
                    <X size={12} className="cursor-pointer" onClick={() => removeFromArray("system_touchpoints", i)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Bottlenecks</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder="Add bottleneck"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("bottlenecks", newItem))}
                />
                <Button type="button" size="sm" onClick={() => addToArray("bottlenecks", newItem)}>
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {formData.bottlenecks.map((b, i) => (
                  <Badge key={i} variant="destructive" className="gap-1">
                    {b}
                    <X size={12} className="cursor-pointer" onClick={() => removeFromArray("bottlenecks", i)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Escalation Paths</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newEscalation.from}
                  onChange={(e) => setNewEscalation({ ...newEscalation, from: e.target.value })}
                  placeholder="From"
                  className="flex-1"
                />
                <Input
                  value={newEscalation.to}
                  onChange={(e) => setNewEscalation({ ...newEscalation, to: e.target.value })}
                  placeholder="To"
                  className="flex-1"
                />
                <Button type="button" size="sm" onClick={addEscalation}>
                  <Plus size={16} />
                </Button>
              </div>
              <div className="space-y-1">
                {formData.escalation_paths.map((ep, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-secondary rounded">
                    <span className="text-sm">{ep.from} → {ep.to}</span>
                    <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("escalation_paths", i)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : (department ? "Update Department" : "Create Department")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PROCESS FORM ====================
export function ProcessFormDialog({ open, onOpenChange, process, departments, processes, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    department_id: "",
    description: "",
    steps: [],
    handoffs: [],
    approvals: [],
    dependencies: [],
    estimated_duration: "",
  });
  const [newStep, setNewStep] = useState({ step: 1, name: "", description: "" });
  const [newHandoff, setNewHandoff] = useState({ from: "", to: "", what: "" });
  const [newApproval, setNewApproval] = useState({ name: "", approver: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (process) {
      setFormData({
        name: process.name || "",
        department_id: process.department_id || "",
        description: process.description || "",
        steps: process.steps || [],
        handoffs: process.handoffs || [],
        approvals: process.approvals || [],
        dependencies: process.dependencies || [],
        estimated_duration: process.estimated_duration || "",
      });
      setNewStep({ step: (process.steps?.length || 0) + 1, name: "", description: "" });
    } else {
      setFormData({
        name: "",
        department_id: "",
        description: "",
        steps: [],
        handoffs: [],
        approvals: [],
        dependencies: [],
        estimated_duration: "",
      });
      setNewStep({ step: 1, name: "", description: "" });
    }
  }, [process, open]);

  const handleSave = async () => {
    if (!formData.name || !formData.department_id) {
      toast.error("Name and Department are required");
      return;
    }
    setSaving(true);
    try {
      if (process?.process_id) {
        await axios.patch(`${API}/processes/${process.process_id}`, formData, { withCredentials: true });
        toast.success("Process updated");
      } else {
        await axios.post(`${API}/processes`, formData, { withCredentials: true });
        toast.success("Process created");
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save process");
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    if (newStep.name) {
      setFormData({ ...formData, steps: [...formData.steps, { ...newStep }] });
      setNewStep({ step: formData.steps.length + 2, name: "", description: "" });
    }
  };

  const addHandoff = () => {
    if (newHandoff.from && newHandoff.to && newHandoff.what) {
      setFormData({ ...formData, handoffs: [...formData.handoffs, { ...newHandoff }] });
      setNewHandoff({ from: "", to: "", what: "" });
    }
  };

  const addApproval = () => {
    if (newApproval.name && newApproval.approver) {
      setFormData({ ...formData, approvals: [...formData.approvals, { ...newApproval }] });
      setNewApproval({ name: "", approver: "" });
    }
  };

  const removeFromArray = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{process ? "Edit Process" : "Create New Process"}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="steps">Process Steps</TabsTrigger>
              <TabsTrigger value="flows">Handoffs & Approvals</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Order to Dispatch"
                  />
                </div>
                <div>
                  <Label>Department *</Label>
                  <Select value={formData.department_id} onValueChange={(v) => setFormData({ ...formData, department_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the process..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Estimated Duration</Label>
                <Input
                  value={formData.estimated_duration}
                  onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                  placeholder="e.g., 2-3 weeks"
                />
              </div>

              <div>
                <Label>Dependencies (other processes)</Label>
                <Select onValueChange={(v) => {
                  if (!formData.dependencies.includes(v)) {
                    setFormData({ ...formData, dependencies: [...formData.dependencies, v] });
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Select dependent process" /></SelectTrigger>
                  <SelectContent>
                    {processes.filter(p => p.process_id !== process?.process_id).map((p) => (
                      <SelectItem key={p.process_id} value={p.process_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.dependencies.map((depId, i) => {
                    const dep = processes.find(p => p.process_id === depId);
                    return (
                      <Badge key={i} variant="outline" className="gap-1">
                        {dep?.name || depId}
                        <X size={12} className="cursor-pointer" onClick={() => removeFromArray("dependencies", i)} />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="steps" className="space-y-4">
              <div>
                <Label>Process Steps</Label>
                <div className="space-y-2 mb-2 p-3 border rounded-md bg-secondary/30">
                  <div className="flex gap-2">
                    <Input
                      value={newStep.step}
                      onChange={(e) => setNewStep({ ...newStep, step: parseInt(e.target.value) || 1 })}
                      placeholder="#"
                      className="w-16"
                      type="number"
                    />
                    <Input
                      value={newStep.name}
                      onChange={(e) => setNewStep({ ...newStep, name: e.target.value })}
                      placeholder="Step name"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Textarea
                      value={newStep.description}
                      onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                      placeholder="Step description"
                      rows={2}
                      className="flex-1"
                    />
                    <Button type="button" onClick={addStep} className="self-end">
                      <Plus size={16} className="mr-1" /> Add Step
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {formData.steps.sort((a, b) => a.step - b.step).map((step, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-violet-50 border border-violet-100 rounded">
                      <div className="w-8 h-8 rounded-full bg-violet-200 text-violet-700 text-sm font-bold flex items-center justify-center flex-shrink-0">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{step.name}</p>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </div>
                      <X size={16} className="cursor-pointer text-muted-foreground hover:text-destructive flex-shrink-0" onClick={() => removeFromArray("steps", i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="flows" className="space-y-4">
              <div>
                <Label>Handoffs</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newHandoff.from}
                    onChange={(e) => setNewHandoff({ ...newHandoff, from: e.target.value })}
                    placeholder="From"
                    className="flex-1"
                  />
                  <Input
                    value={newHandoff.to}
                    onChange={(e) => setNewHandoff({ ...newHandoff, to: e.target.value })}
                    placeholder="To"
                    className="flex-1"
                  />
                  <Input
                    value={newHandoff.what}
                    onChange={(e) => setNewHandoff({ ...newHandoff, what: e.target.value })}
                    placeholder="What"
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={addHandoff}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="space-y-1">
                  {formData.handoffs.map((h, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-secondary rounded">
                      <span className="text-sm">{h.from} → {h.to} <span className="text-muted-foreground">({h.what})</span></span>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("handoffs", i)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Approvals Required</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newApproval.name}
                    onChange={(e) => setNewApproval({ ...newApproval, name: e.target.value })}
                    placeholder="Approval name"
                    className="flex-1"
                  />
                  <Input
                    value={newApproval.approver}
                    onChange={(e) => setNewApproval({ ...newApproval, approver: e.target.value })}
                    placeholder="Approver"
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={addApproval}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="space-y-1">
                  {formData.approvals.map((a, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-accent/10 border border-accent/20 rounded">
                      <span className="text-sm"><strong>{a.name}</strong> by {a.approver}</span>
                      <X size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeFromArray("approvals", i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : (process ? "Update Process" : "Create Process")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SYSTEM FORM ====================
export function SystemFormDialog({ open, onOpenChange, system, roles, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    description: "",
    url: "",
    icon: "Database",
    used_by_roles: [],
    used_in_processes: [],
    integration_notes: "",
    api_config: null,
  });
  const [newProcess, setNewProcess] = useState("");
  const [saving, setSaving] = useState(false);

  const systemTypes = ["ERP", "CRM", "PLM", "Project Management", "Forms", "Portal", "Database", "Field Service", "Design Automation", "Analytics", "Other"];
  const iconOptions = ["Database", "Megaphone", "Cube", "ClipboardText", "Globe", "Table", "Wrench", "Kanban", "Blueprint"];

  useEffect(() => {
    if (system) {
      setFormData({
        name: system.name || "",
        type: system.type || "",
        description: system.description || "",
        url: system.url || "",
        icon: system.icon || "Database",
        used_by_roles: system.used_by_roles || [],
        used_in_processes: system.used_in_processes || [],
        integration_notes: system.integration_notes || "",
        api_config: system.api_config || null,
      });
    } else {
      setFormData({
        name: "",
        type: "",
        description: "",
        url: "",
        icon: "Database",
        used_by_roles: [],
        used_in_processes: [],
        integration_notes: "",
        api_config: null,
      });
    }
  }, [system, open]);

  const handleSave = async () => {
    if (!formData.name || !formData.type) {
      toast.error("Name and Type are required");
      return;
    }
    setSaving(true);
    try {
      if (system?.system_id) {
        await axios.patch(`${API}/systems/${system.system_id}`, formData, { withCredentials: true });
        toast.success("System updated");
      } else {
        await axios.post(`${API}/systems`, formData, { withCredentials: true });
        toast.success("System created");
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save system");
    } finally {
      setSaving(false);
    }
  };

  const addRole = (roleId) => {
    if (!formData.used_by_roles.includes(roleId)) {
      setFormData({ ...formData, used_by_roles: [...formData.used_by_roles, roleId] });
    }
  };

  const removeRole = (index) => {
    setFormData({ ...formData, used_by_roles: formData.used_by_roles.filter((_, i) => i !== index) });
  };

  const addProcess = () => {
    if (newProcess && !formData.used_in_processes.includes(newProcess)) {
      setFormData({ ...formData, used_in_processes: [...formData.used_in_processes, newProcess] });
      setNewProcess("");
    }
  };

  const removeProcess = (index) => {
    setFormData({ ...formData, used_in_processes: formData.used_in_processes.filter((_, i) => i !== index) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{system ? "Edit System" : "Create New System"}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="usage">Usage & Links</TabsTrigger>
              <TabsTrigger value="api">API Config</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Zoho CRM"
                  />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {systemTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Icon</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      className={`px-3 py-1 rounded text-xs ${formData.icon === icon ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                      onClick={() => setFormData({ ...formData, icon })}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the system..."
                  rows={3}
                />
              </div>

              <div>
                <Label>System URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </TabsContent>

            <TabsContent value="usage" className="space-y-4">
              <div>
                <Label>Used By Roles</Label>
                <Select onValueChange={addRole}>
                  <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.used_by_roles.map((roleId, i) => {
                    const role = roles.find(r => r.role_id === roleId);
                    return (
                      <Badge key={i} variant="outline" className="gap-1">
                        {role?.title || roleId}
                        <X size={12} className="cursor-pointer" onClick={() => removeRole(i)} />
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Used In Processes</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newProcess}
                    onChange={(e) => setNewProcess(e.target.value)}
                    placeholder="e.g., Lead Management"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addProcess())}
                  />
                  <Button type="button" size="sm" onClick={addProcess}>
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.used_in_processes.map((proc, i) => (
                    <Badge key={i} variant="secondary" className="gap-1 bg-violet-50 text-violet-700">
                      {proc}
                      <X size={12} className="cursor-pointer" onClick={() => removeProcess(i)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Integration Notes</Label>
                <Textarea
                  value={formData.integration_notes}
                  onChange={(e) => setFormData({ ...formData, integration_notes: e.target.value })}
                  placeholder="Any notes about integration, credentials needed, etc."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="api" className="space-y-4">
              <div className="p-4 bg-secondary/50 rounded-md border border-dashed">
                <div className="flex items-center gap-2 mb-3">
                  <Key size={20} className="text-violet-600" />
                  <h4 className="font-semibold">API Configuration</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure API connection for live data integration. Leave empty to use manual URL linking.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label>API Base URL</Label>
                    <Input
                      value={formData.api_config?.base_url || ""}
                      onChange={(e) => setFormData({ ...formData, api_config: { ...formData.api_config, base_url: e.target.value } })}
                      placeholder="https://api.example.com/v1"
                    />
                  </div>
                  <div>
                    <Label>API Key / Token</Label>
                    <Input
                      type="password"
                      value={formData.api_config?.api_key || ""}
                      onChange={(e) => setFormData({ ...formData, api_config: { ...formData.api_config, api_key: e.target.value } })}
                      placeholder="Enter API key (stored securely)"
                    />
                  </div>
                  <div>
                    <Label>Refresh Token (for OAuth)</Label>
                    <Input
                      type="password"
                      value={formData.api_config?.refresh_token || ""}
                      onChange={(e) => setFormData({ ...formData, api_config: { ...formData.api_config, refresh_token: e.target.value } })}
                      placeholder="OAuth refresh token (optional)"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : (system ? "Update System" : "Create System")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
